import apiClient, { safeJson } from '@/api/client';
import { readSharePoolCloseImpact, SharePoolCloseImpact } from './closeCleanup';
import {
    ShareProvisioningView,
    SharedMembershipPool,
    StaffGroupEntitlement,
    StaffMembershipEntitlement,
} from './types';

type SharePoolResponse = {
    sharePool?: SharedMembershipPool;
};

type MembershipEntitlementsResponse = {
    entitlements?: StaffMembershipEntitlement[];
    /** 그룹 수강권은 회차 모델이 달라 서버가 배열을 분리해 내려준다. */
    groupEntitlements?: StaffGroupEntitlement[];
};

type ShareProvisioningResponse = {
    shareProvisioning?: ShareProvisioningView;
};

const parseSharePoolResponse = async (response: Response) => {
    const data = (await safeJson(response)) as SharePoolResponse | null;
    return data?.sharePool ?? null;
};

/**
 * 종료 응답의 `cleanup` — **서버가 실제로 취소한 결과**다.
 *
 * 화면이 보낸 id 개수로 "N건 취소했습니다"라고 말하면 안 된다. 서버는 현재 항목이
 * 확인 집합의 **부분집합**인지만 검사하므로, 그 사이 다른 곳에서 일부가 이미
 * 취소됐다면 보낸 개수보다 적게 취소된다. 실제 수는 서버만 안다.
 */
export type SharePoolCleanupResult = {
    canceledCount: number;
    scheduleIds: string[];
    requestIds: string[];
    truncated: boolean;
};

const readCleanupResult = (value: unknown): SharePoolCleanupResult | null => {
    if (!value || typeof value !== 'object') return null;
    const raw = value as Record<string, unknown>;
    const canceledCount = raw.canceledCount;
    if (typeof canceledCount !== 'number' || !Number.isSafeInteger(canceledCount)) return null;
    const readIds = (v: unknown): string[] =>
        Array.isArray(v) ? v.filter((id): id is string => typeof id === 'string') : [];
    return {
        canceledCount,
        scheduleIds: readIds(raw.scheduleIds),
        requestIds: readIds(raw.requestIds),
        truncated: raw.truncated === true,
    };
};

/**
 * 백엔드는 "조회 권한 없음"과 "대상 없음"을 모두 404로 마스킹한다(존재 노출 방지).
 * 임시회원·타센터 회원처럼 정상적으로 404가 나는 대상이 있으므로, 404를 장애로 취급하면
 * 공유와 무관한 개인 수강권 일정 등록까지 영구히 막힌다. 404는 "공유 권리 없음"으로 읽는다.
 */
const isNotFound = (error: unknown): boolean =>
    (error as { status?: number } | null)?.status === 404;

/**
 * PT·그룹 사용 권리를 한 번에 받아온다.
 *
 * 서버는 두 배열을 같은 응답에 담아 준다. 예전에는 PT용·그룹용 함수가 이 엔드포인트를
 * 각각 호출해 화면 하나가 같은 요청을 두 번 보냈다 — 이 엔드포인트는 schedules 집계를
 * 돌아 비싼 축에 속하므로 그대로 두면 부하가 두 배가 된다.
 */
export const getStaffEntitlements = async ({
    memberId,
    centerId,
    coachId,
}: {
    memberId: string;
    centerId: string;
    /**
     * 담당 강사로 범위를 좁힐 때만 보낸다. 회원 프로필처럼 센터 전체를 봐야 하는 화면은
     * 생략한다 — 다른 강사의 공유 수강권까지 받아야 하기 때문이다.
     */
    coachId?: string;
}): Promise<{
    entitlements: StaffMembershipEntitlement[];
    groupEntitlements: StaffGroupEntitlement[];
}> => {
    try {
        const response = await apiClient.get(
            `v1/members/${memberId}/membership-entitlements`,
            {
                searchParams: { centerId, ...(coachId ? { coachId } : {}) },
            },
        );
        const data = (await safeJson(response)) as MembershipEntitlementsResponse | null;
        return {
            entitlements: Array.isArray(data?.entitlements) ? data!.entitlements! : [],
            groupEntitlements: Array.isArray(data?.groupEntitlements)
                ? data!.groupEntitlements!
                : [],
        };
    } catch (error) {
        if (isNotFound(error)) return { entitlements: [], groupEntitlements: [] };
        throw error;
    }
};

export const getStaffMembershipEntitlements = async (params: {
    memberId: string;
    centerId: string;
    coachId?: string;
}) => (await getStaffEntitlements(params)).entitlements;

/**
 * 같은 엔드포인트의 그룹 배열. PT만 파싱하면 그룹 공유 참여자가 화면에서 통째로 사라진다.
 */
export const getStaffGroupEntitlements = async (params: {
    memberId: string;
    centerId: string;
}) => (await getStaffEntitlements(params)).groupEntitlements;

const parseShareProvisioningResponse = async (response: Response) => {
    const data = (await safeJson(response)) as ShareProvisioningResponse | null;
    if (!data?.shareProvisioning) {
        throw new Error('공유 수강권 설정 상태를 확인하지 못했습니다.');
    }
    return data.shareProvisioning;
};

export const getTemplateShareProvisioning = async (membershipId: string) => {
    const response = await apiClient.get(
        `v1/membership-share-pools/provision-template/${membershipId}`,
    );
    return parseShareProvisioningResponse(response);
};

export const retryTemplateShareProvisioning = async (membershipId: string) => {
    const response = await apiClient.post(
        `v1/membership-share-pools/provision-template/${membershipId}/retry`,
    );
    return parseShareProvisioningResponse(response);
};

/** ky HTTPError(error.response.status)와 커스텀 Error(error.status)의 상태코드를 함께 읽는다. */
const readErrorStatus = (error: unknown): number | undefined => {
    if (!error || typeof error !== 'object') return undefined;
    const status = (error as { status?: unknown }).status;
    if (typeof status === 'number') return status;
    const response = (error as { response?: { status?: unknown } }).response;
    return typeof response?.status === 'number' ? response.status : undefined;
};

/**
 * 공유 풀 조회 공통 처리.
 * 404 SHARE_POOL_NOT_FOUND는 "아직 공유하지 않음"이라는 정상 상태다. 오류로 올리지 않고
 * null을 돌려 화면이 전환 버튼을 그리게 한다.
 */
const fetchSharePool = async (path: string) => {
    let response: Response;
    try {
        response = await apiClient.get(
            path,
            ({
                throwHttpErrors: false,
                skipErrorAlertFor: [404],
            } as any),
        );
    } catch (error) {
        // 401 → 토큰 갱신 후 재요청은 ky 기본 옵션(ky(request))으로 나가므로
        // throwHttpErrors:false가 사라져 404가 HTTPError로 throw된다.
        // 이 경로에서도 "아직 공유하지 않음" null 계약을 동일하게 유지한다.
        if (readErrorStatus(error) === 404) return null;
        throw error;
    }

    if (response.status === 404) return null;
    if (!response.ok) {
        const error = new Error('공유 수강권 정보를 불러오지 못했습니다.') as Error & {
            status?: number;
        };
        error.status = response.status;
        throw error;
    }
    return parseSharePoolResponse(response);
};

/**
 * PT 수강권 상세에서. 소스 ID로 바로 조회한다.
 *
 * includeClosed=true 로 종료 풀까지 받는다. 404 는 '정상 종료'뿐 아니라 '풀 없음'과
 * '센터 스코프 밖이라 마스킹'까지 같은 모양이라, 종료 풀을 받아 status 로 구분하지 않으면
 * 진짜 이상 상태가 '정상 종료'로 위장되고 복구 안내가 사라진다. 사용 기록의 '회원' 열도
 * 풀이 있어야 남는다.
 */
export const getSharedMembershipPoolByMembership = (membershipId: string) =>
    fetchSharePool(`v1/membership-share-pools/by-membership/${membershipId}?includeClosed=true`);

/** 그룹 수강권 상세에서. by-membership과 대칭이다. */
export const getSharedMembershipPoolByMemberTicket = (memberTicketId: string) =>
    fetchSharePool(
        `v1/membership-share-pools/by-member-ticket/${memberTicketId}?includeClosed=true`,
    );

/** 풀 ID 단건 조회. */
export const getSharedMembershipPoolById = (sharePoolId: string) =>
    fetchSharePool(`v1/membership-share-pools/${sharePoolId}`);

export const createSharedMembershipPool = async ({
    centerId,
    sourceMembershipId,
    maxParticipants,
    maxLifetimeParticipants,
    reason,
}: {
    centerId: string;
    sourceMembershipId: string;
    maxParticipants: number;
    maxLifetimeParticipants: number;
    reason: string;
}) => {
    const response = await apiClient.post(
        `v1/centers/${centerId}/membership-share-pools`,
        {
            json: {
                sourceMembershipId,
                maxParticipants,
                maxLifetimeParticipants,
                reason,
            },
        },
    );
    return parseSharePoolResponse(response);
};

/** 그룹 수강권 공유 전환. PT와 소스 필드·경로만 다르고 이후 관리 API는 동일하다. */
export const createGroupSharedMembershipPool = async ({
    centerId,
    sourceMemberTicketId,
    maxParticipants,
    maxLifetimeParticipants,
    reason,
}: {
    centerId: string;
    sourceMemberTicketId: string;
    maxParticipants: number;
    maxLifetimeParticipants: number;
    reason: string;
}) => {
    const response = await apiClient.post(
        `v1/centers/${centerId}/group-membership-share-pools`,
        {
            json: {
                sourceMemberTicketId,
                maxParticipants,
                maxLifetimeParticipants,
                reason,
            },
        },
    );
    return parseSharePoolResponse(response);
};

export const addSharedMembershipParticipant = async ({
    sharePoolId,
    memberId,
}: {
    sharePoolId: string;
    memberId: string;
}) => {
    const response = await apiClient.post(
        `v1/membership-share-pools/${sharePoolId}/participants`,
        { json: { memberId } },
    );
    return parseSharePoolResponse(response);
};

export const removeSharedMembershipParticipant = async ({
    sharePoolId,
    memberId,
    reason,
}: {
    sharePoolId: string;
    memberId: string;
    reason: string;
}) => {
    const response = await apiClient.delete(
        `v1/membership-share-pools/${sharePoolId}/participants/${memberId}`,
        { json: { reason } },
    );
    return parseSharePoolResponse(response);
};

export const updateSharedMembershipParticipantLimits = async ({
    sharePoolId,
    maxParticipants,
    maxLifetimeParticipants,
    reason,
}: {
    sharePoolId: string;
    maxParticipants: number;
    maxLifetimeParticipants: number;
    reason: string;
}) => {
    const response = await apiClient.patch(
        `v1/membership-share-pools/${sharePoolId}/participant-limits`,
        {
            json: {
                maxParticipants,
                maxLifetimeParticipants,
                reason,
            },
        },
    );
    return parseSharePoolResponse(response);
};

/**
 * 종료하면 무엇이 취소되는가. 되돌릴 수 없는 동작 앞의 확인 자료다.
 *
 * 구버전 서버에는 이 엔드포인트가 없어 404 가 난다. 호출부는 그때 정리 흐름을 열지
 * 않고 오늘과 같은 차단을 남긴다.
 */
export const fetchSharePoolClosePreview = async ({
    sharePoolId,
}: {
    sharePoolId: string;
}): Promise<SharePoolCloseImpact | null> => {
    const response = await apiClient.get(
        `v1/membership-share-pools/${sharePoolId}/close-preview`,
    );
    const impact = readSharePoolCloseImpact(await response.json());
    /**
     * **응답이 내가 물어본 풀의 것인지 확인한다.**
     *
     * 잘못 캐시된 응답이나 프록시 오배송이면 다른 풀의 취소 목록을 보고 확인하게 된다.
     * 그 목록이 실제 대상의 부분집합이면 서버 subset 검증도 통과한다.
     */
    if (impact && impact.sharePoolId && impact.sharePoolId !== sharePoolId) return null;
    return impact;
};

export const closeSharedMembershipPool = async ({
    sharePoolId,
    reason,
    confirmCleanup,
    expectedItemIds,
}: {
    sharePoolId: string;
    reason: string;
    /**
     * 참여자의 미래 수업까지 취소하고 종료할지. **관리자가 프리뷰를 확인했을 때만 true.**
     * 보내지 않으면 서버가 오늘과 똑같이 SHARE_MEMBER_HAS_FUTURE_USAGE 로 막는다.
     */
    confirmCleanup?: boolean;
    /**
     * 프리뷰에서 확인한 취소 대상 id **전체**(소유자+참여자 합집합).
     * 하나라도 빠지면 서버가 아무것도 취소하지 않고 SHARE_CLOSE_PLAN_CHANGED 로 거절한다.
     * `confirmCleanup` 만 보내고 이걸 빠뜨리면 정리가 아예 돌지 않는다 — 둘은 짝이다.
     */
    expectedItemIds?: string[];
}) => {
    const response = await apiClient.patch(
        `v1/membership-share-pools/${sharePoolId}/close`,
        {
            json: {
                reason,
                ...(confirmCleanup ? { confirmCleanup: true, expectedItemIds: expectedItemIds ?? [] } : {}),
            },
        },
    );
    const data = (await safeJson(response)) as
        | { sharePool?: unknown; cleanup?: unknown }
        | null;
    return {
        pool: (data?.sharePool as SharedMembershipPool | undefined) ?? null,
        // 정리를 안 한 종료에는 이 키가 없다. 그때는 취소된 것도 없다.
        cleanup: readCleanupResult(data?.cleanup),
    };
};
